import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Route guard that renders children only when the user is authenticated.
 *
 * Three-state rendering:
 *   1. **Loading** — Supabase is hydrating the session from localStorage on
 *      page load. We show a spinner rather than redirecting immediately; without
 *      this gate, a refresh would flash the /auth page before the session is
 *      restored, causing an unnecessary redirect loop.
 *   2. **Unauthenticated** — redirect to /auth using `replace` so the protected
 *      URL is not added to the browser history stack. This means pressing Back
 *      after signing in goes to the page before the protected route, not back
 *      to /auth again.
 *   3. **Authenticated** — render the child route tree normally.
 *
 * All actual row-level access control is enforced server-side by Supabase RLS;
 * this component is a UX guard only, not a security boundary.
 * @param root0
 * @param root0.children
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Wait for session hydration before deciding to redirect. Without this check
  // a page refresh would momentarily show the auth page on every protected route.
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // `replace` keeps the browser history clean — the login page is not pushed
  // onto the stack, so the Back button works as expected after sign-in.
  // Pass the current path as `from` so Auth.tsx can redirect back after login
  // (e.g. deep links that arrive before the user is authenticated).
  if (!user) {
    const from = location.pathname + location.search;
    return <Navigate to="/auth" state={{ from }} replace />;
  }

  return <>{children}</>;
}
